import { Box, Button, Divider, Rating, Skeleton, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Review from "../models/IReview";
import ReviewService from "../services/ReviewService";

interface ReviewListProps { }

const service = new ReviewService();

const ReviewList: React.FC<ReviewListProps> = () => {
    const { t } = useTranslation();

    const [reviews, setReviews] = useState<Review[]>([]);
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(false);
    const pageSize = 10;

    useEffect(() => {
        loadPage(0);
    }, []);

    const loadPage = async (pageIndex: number) => {
        setLoading(true);
        const offset = pageIndex * pageSize;
        const response = await service.getReviews(pageSize, offset);
        setReviews((prev) =>
            pageIndex === 0 ? response.reviews : [...prev, ...response.reviews]
        );
        setHasMore(response.hasMore);
        setPage(pageIndex);
        setLoading(false);
    };

    const renderSkeletons = () => (
        <>
            {[...Array(3)].map((_, i) => (
                <Box key={i} sx={{ mb: 3, display: "flex", flexDirection: "column" }}>
                    <Skeleton variant="text" width="40%"></Skeleton>
                    <Skeleton variant="rectangular" height={20} width="120px" sx={{ mt: 1 }} />
                    <Skeleton variant="text" width="80%" sx={{ mt: 1 }} />
                    <Skeleton variant="text" width="60%" />
                    <Divider sx={{ mt: 2 }} />
                </Box>
            ))}
        </>
    );

    return (
        <Box>
            {loading && reviews.length === 0 ? (
                renderSkeletons()
            ) : reviews.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                    {t("review.empty")}
                </Typography>
            ) : (
                reviews.map((review) => (
                    <Box key={review.id} sx={{ mb: 3, display: "flex", flexDirection: "column" }}>
                        <Typography variant="subtitle1" fontWeight="bold">
                            {review.userName}
                        </Typography>
                        <Rating value={review.rating} readOnly size="small" />
                        {review.comment && (
                            <Typography variant="body2" sx={{ mt: 1 }}>
                                {review.comment}
                            </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary">
                            {new Date(review.dateCreated).toLocaleDateString("hr-HR")}
                        </Typography>
                        <Divider sx={{ mt: 2 }} />
                    </Box>
                ))
            )}

            {hasMore && !loading && (
                <Box sx={{ mt: 2 }}>
                    <Button variant="outlined" color="secondary" onClick={() => loadPage(page + 1)}>
                        <Typography>
                            {t("review.loadMore")}
                        </Typography>
                    </Button>

                </Box>
            )}

            {loading && reviews.length > 0 && renderSkeletons()}
        </Box>
    );
};

export default ReviewList;
